import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  SignOut,
  ClipboardText,
  Star,
  Images,
  Phone,
  EnvelopeSimple,
  MapPin,
  CircleNotch,
  Check,
  X,
  Trash,
  UploadSimple,
  PlusCircle,
  VideoCamera,
  ArrowSquareOut,
  CurrencyDollar,
  FloppyDisk,
  PaperPlaneRight,
  Article,
  PencilSimple,
  ArrowLeft,
  CalendarCheck,
} from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import JobStoriesTab from "@/components/JobStoriesTab";
import BookingsTab from "@/components/BookingsTab";
import CalcItemsEditor from "@/components/CalcItemsEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return d;
  }
};

const Empty = ({ icon: Icon, label }) => (
  <div className="border-2 border-dashed border-[#D4D4D8] rounded-sm py-16 flex flex-col items-center text-center gap-3">
    <Icon size={40} weight="duotone" className="text-[#46A01E]" />
    <p className="font-display font-bold text-[#15306E]">{label}</p>
  </div>
);

const Loader = () => (
  <div className="flex justify-center py-16">
    <CircleNotch size={32} weight="bold" className="animate-spin text-[#46A01E]" />
  </div>
);

function LeadsTab() {
  const [quotes, setQuotes] = useState(null);

  useEffect(() => {
    api.get("/quotes").then(({ data }) => setQuotes(data)).catch(() => setQuotes([]));
  }, []);

  if (quotes === null) return <Loader />;
  if (quotes.length === 0) return <Empty icon={ClipboardText} label="No quote requests yet" />;

  return (
    <div className="space-y-4" data-testid="admin-leads-list">
      {quotes.map((q) => (
        <div key={q.id} data-testid={`admin-lead-${q.id}`} className="border border-[#E4E4E7] rounded-sm p-5 bg-white">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-display font-extrabold text-lg text-[#15306E]">{q.name}{q.company ? ` — ${q.company}` : ""}</h3>
              {(q.business_type || q.pickup_hours) && (
                <p className="text-xs text-[#52525B] mt-0.5" data-testid={`admin-lead-biz-${q.id}`}>Commercial · {[q.business_type, q.pickup_hours].filter(Boolean).join(" · ")}</p>
              )}
              <span className="inline-block mt-1 bg-[#EAF4E3] text-[#15306E] text-xs font-bold px-2 py-1 rounded-sm">
                {q.preferred_time || q.service || "—"}
              </span>
            </div>
            <span className="text-xs text-[#52525B]">{fmtDate(q.created_at)}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-4 text-sm text-[#3F3F46]">
            <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-[#46A01E]">
              <Phone size={15} weight="fill" className="text-[#46A01E]" /> {q.phone}
            </a>
            {q.email && (
              <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-[#46A01E]">
                <EnvelopeSimple size={15} weight="fill" className="text-[#46A01E]" /> {q.email}
              </a>
            )}
            {q.address && (
              <div className="flex items-center gap-2">
                <MapPin size={15} weight="fill" className="text-[#46A01E]" /> {q.address}
              </div>
            )}
          </div>
          {q.details && <p className="mt-3 text-sm text-[#52525B] leading-relaxed">{q.details}</p>}
          {q.photos?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {q.photos.map((p, i) => (
                <a
                  key={i}
                  href={`${BACKEND}${p.url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="relative w-20 h-20 rounded-sm overflow-hidden border border-[#E4E4E7] group"
                >
                  <img src={`${BACKEND}${p.url}`} alt={p.filename || "photo"} className="w-full h-full object-cover" />
                  <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <ArrowSquareOut size={18} weight="bold" className="text-white" />
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ReviewsTab() {
  const [reviews, setReviews] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/reviews").then(({ data }) => setReviews(data)).catch(() => setReviews([]));
  }, []);
  useEffect(load, [load]);

  const setApproval = async (id, approved) => {
    setBusy(id);
    try {
      await api.patch(`/admin/reviews/${id}`, { approved });
      toast.success(approved ? "Review approved — now live on site." : "Review hidden.");
      load();
    } catch {
      toast.error("Update failed.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusy(null);
    }
  };

  if (reviews === null) return <ReviewsWrap><Loader /></ReviewsWrap>;
  if (reviews.length === 0)
    return (
      <ReviewsWrap>
        <Empty icon={Star} label="No reviews submitted yet" />
      </ReviewsWrap>
    );

  return (
    <ReviewsWrap>
      <div className="space-y-4" data-testid="admin-reviews-list">
        {reviews.map((r) => (
        <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-[#E4E4E7] rounded-sm p-5 bg-white">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display font-extrabold text-lg text-[#15306E]">{r.name}</h3>
                {r.approved ? (
                  <span className="bg-[#EAF4E3] text-[#2F6B12] text-xs font-bold px-2 py-0.5 rounded-sm">Approved</span>
                ) : (
                  <span className="bg-[#FEF3C7] text-[#92400E] text-xs font-bold px-2 py-0.5 rounded-sm" data-testid="review-pending-badge">Pending</span>
                )}
              </div>
              <div className="flex items-center gap-1 mt-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={15} weight="fill" className={i < r.rating ? "text-[#F5A623]" : "text-[#D4D4D8]"} />
                ))}
                {r.location && <span className="text-xs text-[#52525B] ml-2">{r.location}</span>}
              </div>
            </div>
            <span className="text-xs text-[#52525B]">{fmtDate(r.created_at)}</span>
          </div>
          {r.service && <span className="inline-block mt-2 text-xs text-[#52525B]">Service: {r.service}</span>}
          <p className="mt-2 text-sm text-[#3F3F46] leading-relaxed">{r.text}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {!r.approved ? (
              <button
                data-testid={`approve-review-${r.id}`}
                disabled={busy === r.id}
                onClick={() => setApproval(r.id, true)}
                className="flex items-center gap-1.5 bg-[#46A01E] hover:bg-[#3A8418] text-white text-sm font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60"
              >
                <Check size={15} weight="bold" /> Approve
              </button>
            ) : (
              <button
                data-testid={`unapprove-review-${r.id}`}
                disabled={busy === r.id}
                onClick={() => setApproval(r.id, false)}
                className="flex items-center gap-1.5 border border-[#E4E4E7] hover:border-[#15306E] text-[#15306E] text-sm font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60"
              >
                <X size={15} weight="bold" /> Hide
              </button>
            )}
            <button
              data-testid={`delete-review-${r.id}`}
              disabled={busy === r.id}
              onClick={() => remove(r.id)}
              className="flex items-center gap-1.5 border border-[#F3D0D0] text-[#C0392B] hover:bg-[#FBEAEA] text-sm font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60"
            >
              <Trash size={15} weight="bold" /> Delete
            </button>
          </div>
        </div>
      ))}
      </div>
    </ReviewsWrap>
  );
}

function GalleryTab() {
  const [items, setItems] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/gallery").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const isVideo = f.type.startsWith("video/");
    if (!f.type.startsWith("image/") && !isVideo) {
      toast.error("Please choose an image or video file.");
      return;
    }
    const max = isVideo ? 100 : 15;
    if (f.size > max * 1024 * 1024) {
      toast.error(`File too large (max ${max}MB).`);
      return;
    }
    setFile(f);
    setPreview({ url: URL.createObjectURL(f), isVideo });
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a photo or video first.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Added to the gallery.");
      setFile(null);
      setPreview(null);
      setCaption("");
      load();
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Removed from gallery.");
      load();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={upload} data-testid="admin-gallery-upload-form" className="border border-[#E4E4E7] rounded-sm p-5 bg-white space-y-3">
        <h3 className="font-display font-extrabold text-lg text-[#15306E]">Add a job photo / video</h3>
        <input ref={fileRef} type="file" accept="image/*,video/*" onChange={pickFile} className="hidden" data-testid="admin-gallery-file-input" />
        {!preview ? (
          <button
            type="button"
            data-testid="admin-gallery-choose-button"
            onClick={() => fileRef.current?.click()}
            className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#46A01E] text-[#15306E] bg-[#F4FAF0] hover:bg-[#EAF4E3] py-10 rounded-sm transition-colors"
          >
            <UploadSimple size={30} weight="duotone" className="text-[#46A01E]" />
            <span className="font-semibold text-sm">Click to choose (images ≤15MB, videos ≤100MB)</span>
          </button>
        ) : (
          <div className="relative rounded-sm overflow-hidden border border-[#E4E4E7]">
            {preview.isVideo ? (
              <video src={preview.url} className="w-full max-h-64 object-cover" controls />
            ) : (
              <img src={preview.url} alt="preview" className="w-full max-h-64 object-cover" />
            )}
            <button
              type="button"
              onClick={() => { setFile(null); setPreview(null); }}
              className="absolute top-2 right-2 bg-black/60 hover:bg-[#46A01E] text-white rounded-full p-1 transition-colors"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        )}
        <input
          data-testid="admin-gallery-caption-input"
          className="w-full border border-[#E4E4E7] px-4 py-3 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#46A01E]"
          placeholder="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
        <button
          type="submit"
          disabled={uploading}
          data-testid="admin-gallery-upload-button"
          className="flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm px-6 py-3 rounded-sm transition-colors disabled:opacity-70"
        >
          {uploading ? <CircleNotch size={18} weight="bold" className="animate-spin" /> : <><PlusCircle size={18} weight="fill" /> Add to Gallery</>}
        </button>
      </form>

      {items === null ? (
        <Loader />
      ) : items.length === 0 ? (
        <Empty icon={Images} label="No gallery items yet" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-gallery-grid">
          {items.map((it) => (
            <div key={it.id} data-testid={`admin-gallery-item-${it.id}`} className="group relative aspect-square rounded-sm overflow-hidden border border-[#E4E4E7] bg-black">
              {it.media_type === "video" ? (
                <video src={`${BACKEND}${it.media_url}`} className="w-full h-full object-cover" preload="metadata" />
              ) : (
                <img src={`${BACKEND}${it.media_url}`} alt={it.caption || "job"} className="w-full h-full object-cover" />
              )}
              {it.media_type === "video" && (
                <span className="absolute top-2 left-2 flex items-center gap-1 bg-[#15306E]/90 text-white text-xs font-semibold px-2 py-1 rounded-sm">
                  <VideoCamera size={12} weight="fill" /> Video
                </span>
              )}
              <button
                data-testid={`delete-gallery-${it.id}`}
                disabled={busy === it.id}
                onClick={() => remove(it.id)}
                className="absolute top-2 right-2 bg-black/60 hover:bg-[#C0392B] text-white rounded-full p-1.5 transition-colors disabled:opacity-60"
              >
                {busy === it.id ? <CircleNotch size={15} weight="bold" className="animate-spin" /> : <Trash size={15} weight="bold" />}
              </button>
              {it.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                  <p className="text-white text-xs font-medium leading-tight">{it.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PricingTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setError(false);
    setData(null);
    api.get("/pricing").then(({ data }) => setData(data)).catch(() => setError(true));
  };

  useEffect(() => { load(); }, []);

  if (error)
    return (
      <div className="text-center py-12" data-testid="admin-pricing-error">
        <p className="text-[#52525B] mb-4">Couldn't load pricing.</p>
        <button onClick={load} className="bg-[#15306E] text-white px-4 py-2 rounded-sm text-sm font-bold">Retry</button>
      </div>
    );
  if (data === null) return <Loader />;

  const setTierPrice = (i, v) => {
    const tiers = data.tiers.map((t, idx) => (idx === i ? { ...t, price: Number(v) || 0 } : t));
    setData({ ...data, tiers });
  };
  const setItemPrice = (ci, ii, v) => {
    const perItem = data.perItem.map((g, gi) =>
      gi === ci ? { ...g, items: g.items.map((it, idx) => (idx === ii ? [it[0], v] : it)) } : g
    );
    setData({ ...data, perItem });
  };
  const setFee = (i, v) => {
    const recycleFees = data.recycleFees.map((f, idx) => (idx === i ? { ...f, price: v } : f));
    setData({ ...data, recycleFees });
  };

  const save = async () => {
    const bad = (data.calcItems || []).find((c) => !c.category.trim() || c.items.some((i) => !i.name.trim()) || new Set(c.items.map((i) => i.name.trim().toLowerCase())).size !== c.items.length);
    if (bad) { toast.error(`Item Calculator: check the "${bad.category || "unnamed"}" tab — every tab and item needs a name, and names can't repeat within a tab.`); return; }
    setSaving(true);
    try {
      await api.put("/admin/pricing", { tiers: data.tiers, perItem: data.perItem, recycleFees: data.recycleFees, curbsideDiscount: data.curbsideDiscount ?? 30, overnightSurchargePct: data.overnightSurchargePct ?? 25, calcItems: data.calcItems });
      toast.success("Pricing updated — live on the site.");
    } catch (e) {
      toast.error("Couldn't save pricing. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const numCls = "w-24 border border-[#E4E4E7] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#46A01E]";
  const txtCls = "w-28 border border-[#E4E4E7] px-3 py-2 text-sm rounded-sm text-right focus:outline-none focus:ring-2 focus:ring-[#46A01E]";

  return (
    <div className="space-y-8" data-testid="admin-pricing">
      <div className="flex items-center justify-between gap-4 sticky top-0 bg-[#F4F4F5] py-2 z-10">
        <p className="text-sm text-[#52525B]">Edit prices below, then Save. Changes go live on your Pricing page and Estimate tool.</p>
        <button
          data-testid="admin-pricing-save"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm px-5 py-2.5 rounded-sm transition-colors disabled:opacity-70 shrink-0"
        >
          {saving ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <FloppyDisk size={16} weight="fill" />} Save
        </button>
      </div>

      {/* Load tiers */}
      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
        <h3 className="font-display font-extrabold text-[#15306E] mb-1">Load-Based Pricing (Full-Service)</h3>
        <div className="flex flex-wrap items-center gap-2 mb-4 text-sm text-[#52525B]">
          <span>Curbside discount off full-service:</span>
          <input data-testid="admin-curbside-discount" type="number" min={0} max={90} value={data.curbsideDiscount ?? 30}
            onChange={(e) => setData({ ...data, curbsideDiscount: Math.min(90, Math.max(0, Number(e.target.value) || 0)) })} className={numCls} />
          <span>%</span>
          <span className="ml-4">Overnight pickup surcharge:</span>
          <input data-testid="admin-overnight-surcharge" type="number" min={0} max={200} value={data.overnightSurchargePct ?? 25}
            onChange={(e) => setData({ ...data, overnightSurchargePct: Math.min(200, Math.max(0, Number(e.target.value) || 0)) })} className={numCls} />
          <span>% of the estimate</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.tiers.map((t, i) => (
            <div key={t.label} className="flex items-center justify-between gap-2 border border-[#F0F0F1] rounded-sm px-3 py-2">
              <div>
                <p className="font-semibold text-[#15306E] text-sm">{t.label}</p>
                <p className="text-xs text-[#71717A]">{t.yards}</p>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[#46A01E] font-bold">$</span>
                <input data-testid={`admin-tier-${i}`} type="number" value={t.price} onChange={(e) => setTierPrice(i, e.target.value)} className={numCls} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-item */}
      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
        <h3 className="font-display font-extrabold text-[#15306E] mb-4">Single-Item Pricing</h3>
        <div className="grid md:grid-cols-2 gap-6">
          {data.perItem.map((g, ci) => (
            <div key={g.category}>
              <p className="font-display font-bold text-[#15306E] text-sm uppercase tracking-wide mb-2">{g.category}</p>
              <ul className="space-y-1.5">
                {g.items.map((it, ii) => (
                  <li key={it[0]} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-[#3F3F46]">{it[0]}</span>
                    <input data-testid={`admin-item-${ci}-${ii}`} value={it[1]} onChange={(e) => setItemPrice(ci, ii, e.target.value)} className={txtCls} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Recycle fees */}
      {data.calcItems && <CalcItemsEditor cats={data.calcItems} onChange={(calcItems) => setData({ ...data, calcItems })} />}

      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
        <h3 className="font-display font-extrabold text-[#15306E] mb-4">Recycling / Special-Handling Fees</h3>
        <ul className="space-y-2">
          {data.recycleFees.map((f, i) => (
            <li key={f.item} className="flex items-center justify-between gap-3">
              <span className="text-sm text-[#3F3F46]">{f.item}</span>
              <input data-testid={`admin-fee-${i}`} value={f.price} onChange={(e) => setFee(i, e.target.value)} className={txtCls} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const REVIEW_LINK = "https://search.google.com/local/writereview?placeid=ChIJx8Eov0VYD6wRPjfrK_i3LIE";

function ReviewRequestPanel() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const smsText = `Thanks for choosing CleanOut247! If we did a great job, would you mind leaving us a quick Google review? It really helps our small local business. ${REVIEW_LINK}`;

  const sendEmail = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid customer email."); return; }
    setSending(true);
    try {
      await api.post("/admin/review-request", { name, email });
      toast.success("Review request emailed to your customer!");
      setEmail(""); setName("");
    } catch {
      toast.error("Couldn't send the email. Try again.");
    } finally {
      setSending(false);
    }
  };
  const copySms = async () => {
    try { await navigator.clipboard.writeText(smsText); toast.success("Message copied — paste it into a text!"); }
    catch { toast.error("Copy failed."); }
  };

  return (
    <div data-testid="review-request-panel" className="bg-[#0B1E45] text-white rounded-sm p-5">
      <h3 className="font-display font-extrabold text-lg flex items-center gap-2"><Star weight="fill" className="text-[#F5A623]" /> Ask a customer for a review</h3>
      <p className="text-white/60 text-sm mt-1">More 5-star Google reviews boost your local ranking. Send a request in one tap.</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div className="bg-white/5 rounded-sm p-4">
          <p className="font-bold text-sm mb-2">Email it</p>
          <input data-testid="rr-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name (optional)" className="w-full mb-2 bg-white text-[#0A0A0A] px-3 py-2 rounded-sm text-sm" />
          <input data-testid="rr-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@email.com" className="w-full mb-2 bg-white text-[#0A0A0A] px-3 py-2 rounded-sm text-sm" />
          <button data-testid="rr-send-email" onClick={sendEmail} disabled={sending} className="w-full flex items-center justify-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-bold text-sm px-4 py-2.5 rounded-sm disabled:opacity-60">
            {sending ? <CircleNotch className="animate-spin" size={16} /> : <PaperPlaneRight size={16} weight="fill" />} Send email
          </button>
        </div>
        <div className="bg-white/5 rounded-sm p-4">
          <p className="font-bold text-sm mb-2">Text it from your phone</p>
          <p className="text-white/60 text-xs mb-2">"{smsText}"</p>
          <div className="flex gap-2">
            <a data-testid="rr-sms-link" href={`sms:?&body=${encodeURIComponent(smsText)}`} className="flex-1 text-center bg-white text-[#15306E] font-bold text-sm px-3 py-2.5 rounded-sm">Open Messages</a>
            <button data-testid="rr-copy-sms" onClick={copySms} className="flex-1 border border-white/30 hover:bg-white/10 text-white font-bold text-sm px-3 py-2.5 rounded-sm">Copy text</button>
          </div>
        </div>
      </div>
      <a
        data-testid="rr-open-qr"
        href="/review"
        target="_blank"
        rel="noreferrer"
        className="mt-3 flex items-center justify-center gap-2 border border-white/30 hover:bg-white/10 text-white font-bold text-sm px-4 py-2.5 rounded-sm"
      >
        <Star size={16} weight="fill" className="text-[#F5A623]" /> Open the on-site review page (QR code to show customers)
      </a>
    </div>
  );
}

function ReviewsWrap({ children }) {
  return (
    <div className="space-y-6">
      <ReviewRequestPanel />
      {children}
    </div>
  );
}

const BLANK_GUIDE = {
  title: "", category: "Guide", description: "", excerpt: "", readTime: "5 min read",
  hero: "", body: [{ type: "p", text: "" }], faqs: [], published: true,
};
const BLOCK_TYPES = [
  { value: "p", label: "Paragraph" },
  { value: "h2", label: "Heading" },
  { value: "ul", label: "Bullet list" },
  { value: "cta", label: "Call-to-action" },
];

function GuideEditor({ guide, onDone }) {
  const isNew = !guide?.id;
  const [f, setF] = useState(() => ({
    ...BLANK_GUIDE,
    ...guide,
    body: (guide?.body?.length ? guide.body : BLANK_GUIDE.body).map((b) => ({ ...b })),
    faqs: (guide?.faqs || []).map((q) => ({ ...q })),
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const setBlock = (i, patch) => set("body", f.body.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const addBlock = () => set("body", [...f.body, { type: "p", text: "" }]);
  const delBlock = (i) => set("body", f.body.filter((_, idx) => idx !== i));
  const setFaq = (i, patch) => set("faqs", f.faqs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const addFaq = () => set("faqs", [...f.faqs, { q: "", a: "" }]);
  const delFaq = (i) => set("faqs", f.faqs.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!f.title.trim() || !f.description.trim()) { toast.error("Title and description are required."); return; }
    const payload = {
      ...f,
      body: f.body.map((b) => (b.type === "ul"
        ? { type: "ul", items: (b.items || (b.text || "").split("\n")).map((s) => s.trim()).filter(Boolean) }
        : { type: b.type, text: b.text || "" })),
      faqs: f.faqs.filter((q) => q.q.trim() && q.a.trim()),
    };
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/guides", payload);
      else await api.put(`/admin/guides/${guide.id}`, payload);
      toast.success(isNew ? "Guide published!" : "Guide updated!");
      onDone(true);
    } catch (e) {
      toast.error("Couldn't save the guide.");
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full border border-[#E4E4E7] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#46A01E]";

  return (
    <div className="space-y-5" data-testid="guide-editor">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => onDone(false)} data-testid="guide-editor-back" className="flex items-center gap-1.5 text-[#15306E] font-bold text-sm"><ArrowLeft size={16} weight="bold" /> Back to guides</button>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-[#3F3F46]">
            <input type="checkbox" data-testid="guide-published" checked={f.published} onChange={(e) => set("published", e.target.checked)} /> Published
          </label>
          <button onClick={save} disabled={saving} data-testid="guide-save" className="flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm px-5 py-2.5 rounded-sm disabled:opacity-60">
            {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="fill" />} Save
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5 space-y-3">
        <div><label className="text-xs font-bold text-[#71717A] uppercase">Title</label><input data-testid="guide-title" className={inp} value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs font-bold text-[#71717A] uppercase">Category</label><input className={inp} value={f.category} onChange={(e) => set("category", e.target.value)} /></div>
          <div><label className="text-xs font-bold text-[#71717A] uppercase">Read time</label><input className={inp} value={f.readTime} onChange={(e) => set("readTime", e.target.value)} /></div>
          <div><label className="text-xs font-bold text-[#71717A] uppercase">Cover image URL</label><input data-testid="guide-hero" className={inp} value={f.hero} onChange={(e) => set("hero", e.target.value)} /></div>
        </div>
        <div><label className="text-xs font-bold text-[#71717A] uppercase">Meta description (for Google)</label><textarea className={inp} rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
        <div><label className="text-xs font-bold text-[#71717A] uppercase">Card excerpt</label><textarea className={inp} rows={2} value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></div>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
        <div className="flex items-center justify-between mb-3"><h4 className="font-display font-extrabold text-[#15306E]">Article content</h4><button onClick={addBlock} data-testid="guide-add-block" className="flex items-center gap-1 text-[#46A01E] font-bold text-sm"><PlusCircle size={16} weight="fill" /> Add block</button></div>
        <div className="space-y-3">
          {f.body.map((b, i) => (
            <div key={i} className="border border-[#F0F0F1] rounded-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <select value={b.type} onChange={(e) => setBlock(i, e.target.value === "ul" ? { type: "ul", items: b.items || (b.text || "").split("\n"), text: undefined } : { type: e.target.value })} className="border border-[#E4E4E7] text-sm rounded-sm px-2 py-1">
                  {BLOCK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <button onClick={() => delBlock(i)} className="text-[#C0392B] text-xs font-bold flex items-center gap-1"><Trash size={13} /> Remove</button>
              </div>
              {b.type === "ul" ? (
                <textarea data-testid={`guide-block-${i}`} className={inp} rows={4} placeholder="One bullet per line" value={(b.items || []).join("\n")} onChange={(e) => setBlock(i, { items: e.target.value.split("\n") })} />
              ) : (
                <textarea data-testid={`guide-block-${i}`} className={inp} rows={b.type === "h2" ? 1 : 3} placeholder={b.type === "cta" ? "Call-to-action text" : b.type === "h2" ? "Heading" : "Paragraph text"} value={b.text || ""} onChange={(e) => setBlock(i, { text: e.target.value })} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
        <div className="flex items-center justify-between mb-3"><h4 className="font-display font-extrabold text-[#15306E]">FAQs (great for Google)</h4><button onClick={addFaq} data-testid="guide-add-faq" className="flex items-center gap-1 text-[#46A01E] font-bold text-sm"><PlusCircle size={16} weight="fill" /> Add FAQ</button></div>
        <div className="space-y-3">
          {f.faqs.map((q, i) => (
            <div key={i} className="border border-[#F0F0F1] rounded-sm p-3 space-y-2">
              <div className="flex items-center justify-between"><span className="text-xs font-bold text-[#71717A]">Q{i + 1}</span><button onClick={() => delFaq(i)} className="text-[#C0392B] text-xs font-bold flex items-center gap-1"><Trash size={13} /> Remove</button></div>
              <input className={inp} placeholder="Question" value={q.q} onChange={(e) => setFaq(i, { q: e.target.value })} />
              <textarea className={inp} rows={2} placeholder="Answer" value={q.a} onChange={(e) => setFaq(i, { a: e.target.value })} />
            </div>
          ))}
          {f.faqs.length === 0 && <p className="text-sm text-[#71717A]">No FAQs yet.</p>}
        </div>
      </div>
    </div>
  );
}

function GuidesTab() {
  const [guides, setGuides] = useState(null);
  const [editing, setEditing] = useState(null); // guide object | 'new' | null
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/guides").then(({ data }) => setGuides(data)).catch(() => setGuides([]));
  }, []);
  useEffect(load, [load]);

  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.title}"? This can't be undone.`)) return;
    setBusy(g.id);
    try { await api.delete(`/admin/guides/${g.id}`); toast.success("Guide deleted."); load(); }
    catch { toast.error("Delete failed."); }
    finally { setBusy(null); }
  };

  if (editing) {
    return (
      <GuideEditor
        guide={editing === "new" ? null : editing}
        onDone={(changed) => { setEditing(null); if (changed) load(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-guides">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#52525B]">Create and edit blog guides. Published guides appear on your /blog page.</p>
        <button data-testid="guide-new" onClick={() => setEditing("new")} className="flex items-center gap-2 bg-[#15306E] hover:bg-[#0B1E45] text-white font-display font-extrabold uppercase tracking-wide text-sm px-5 py-2.5 rounded-sm">
          <PlusCircle size={16} weight="fill" /> New guide
        </button>
      </div>
      {guides === null ? <Loader /> : guides.length === 0 ? <Empty icon={Article} label="No guides yet" /> : (
        <div className="space-y-3">
          {guides.map((g) => (
            <div key={g.id} data-testid={`guide-row-${g.slug}`} className="flex items-center gap-4 border border-[#E4E4E7] rounded-sm p-4 bg-white">
              {g.hero && <img src={g.hero} alt="" className="w-16 h-16 object-cover rounded-sm shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-display font-extrabold text-[#15306E] truncate">{g.title}</p>
                  {g.published ? <span className="bg-[#EAF4E3] text-[#2F6B12] text-xs font-bold px-2 py-0.5 rounded-sm">Live</span> : <span className="bg-[#FEF3C7] text-[#92400E] text-xs font-bold px-2 py-0.5 rounded-sm">Draft</span>}
                </div>
                <p className="text-xs text-[#71717A] mt-0.5 truncate">/{g.slug} · {g.category}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`/blog/${g.slug}`} target="_blank" rel="noreferrer" className="p-2 text-[#15306E] hover:bg-[#F4F4F5] rounded-sm" title="View"><ArrowSquareOut size={18} /></a>
                <button data-testid={`guide-edit-${g.slug}`} onClick={() => setEditing(g)} className="flex items-center gap-1.5 border border-[#E4E4E7] hover:border-[#15306E] text-[#15306E] text-sm font-bold px-3 py-2 rounded-sm"><PencilSimple size={15} weight="bold" /> Edit</button>
                <button disabled={busy === g.id} onClick={() => remove(g)} className="p-2 text-[#C0392B] hover:bg-[#FBEAEA] rounded-sm" title="Delete"><Trash size={18} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <header className="bg-[#0B1E45] text-white px-5 lg:px-8 py-5">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div className="font-display font-black text-xl tracking-tighter">
            CleanOut<span className="text-[#5DB82F]">247</span>
            <span className="ml-2 text-xs font-bold uppercase tracking-widest text-[#5DB82F]">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-white/70" data-testid="admin-user-email">{user?.email}</span>
            <button
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="flex items-center gap-1.5 border border-white/20 hover:bg-white/10 text-white text-sm font-bold px-4 py-2 rounded-sm transition-colors"
            >
              <SignOut size={16} weight="bold" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-8">
        <h1 className="font-display font-black tracking-tighter text-3xl text-[#15306E] mb-6">Dashboard</h1>
        <Tabs defaultValue="leads">
          <TabsList className="mb-6" data-testid="admin-tabs">
            <TabsTrigger value="leads" data-testid="tab-leads"><ClipboardText size={16} weight="bold" className="mr-1.5" /> Leads</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings"><CalendarCheck size={16} weight="bold" className="mr-1.5" /> Bookings</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews"><Star size={16} weight="bold" className="mr-1.5" /> Reviews</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery"><Images size={16} weight="bold" className="mr-1.5" /> Gallery</TabsTrigger>
            <TabsTrigger value="stories" data-testid="tab-stories"><Images size={16} weight="bold" className="mr-1.5" /> Job Stories</TabsTrigger>
            <TabsTrigger value="guides" data-testid="tab-guides"><Article size={16} weight="bold" className="mr-1.5" /> Guides</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing"><CurrencyDollar size={16} weight="bold" className="mr-1.5" /> Pricing</TabsTrigger>
          </TabsList>
          <TabsContent value="leads"><LeadsTab /></TabsContent>
          <TabsContent value="bookings"><BookingsTab /></TabsContent>
          <TabsContent value="reviews"><ReviewsTab /></TabsContent>
          <TabsContent value="gallery"><GalleryTab /></TabsContent>
          <TabsContent value="stories"><JobStoriesTab /></TabsContent>
          <TabsContent value="guides"><GuidesTab /></TabsContent>
          <TabsContent value="pricing"><PricingTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
